import { useLiveQuery } from 'dexie-react-hooks'
import jwtDecode from 'jwt-decode'
import moment from 'moment'
import React, { useEffect, useState, useRef } from 'react'
import { useMutation, useQuery } from 'react-apollo'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { Button, Footer, Header, HistoryItem, Modal } from '../../components'
import { GET_DELIVERIES, GET_USER, UPDATE_STATUS } from '../../gql'
import useNetwork from '../../hooks/useNetwork'
import { db } from '../../lib/db'
import './HistoryStyle.scss'

const History = () => {
  const { t } = useTranslation('history')
  const history = useHistory()
  const [search, setSearch] = useState('')
  const [period, setPeriod] = useState('0')
  const [deliveries, setDeliveries] = useState([])
  const [loading, setLoading] = useState(true)

  // États pour la modale d'exportation
  const [showExportModal, setShowExportModal] = useState(false)
  const [password, setPassword] = useState('')
  const [passwordError, setPasswordError] = useState(false)

  // Référence pour le compteur de clics
  const clickCountRef = useRef(0)
  const clickTimerRef = useRef(null)

  // Mot de passe en dur
  const EXPORT_PASSWORD = process.env.REACT_APP_EXPORT_PASSWORD

  const syncTime = useLiveQuery(async () => {
    const dt = await db.lastSync.toArray()
    return dt[0]
  })

  const offlineDeliveries = useLiveQuery(async () => {
    const offDeliveries = await db.deliveries.toArray()
    return offDeliveries
  }, [])

  const network = useNetwork()
  const { data: userData } = useQuery(GET_USER, {
    variables: {
      id: localStorage.getItem('access_token')
        ? jwtDecode(localStorage.getItem('access_token')).id
        : '',
    },
  })

  const { data } = useQuery(GET_DELIVERIES, {
    fetchPolicy: network ? 'cache-and-network' : 'cache-only',
    onCompleted: () => {
      setLoading(false)
    },
    variables: {
      site: userData?.user?.idSite,
      startDate: network
        ? moment().subtract(1, 'week').format('yyyy-MM-DD')
        : moment().subtract(1, 'day').format('yyyy-MM-DD'),
      endDate: moment().add(1, 'day').format('yyyy-MM-DD'),
    },
  })

  const [updateStatus] = useMutation(UPDATE_STATUS)

  useEffect(() => {
    if ((network && data) || (!network && offlineDeliveries)) {
      const sortedData = network
        ? data?.draftOutboundDeliveries.nodes.sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
          )
        : offlineDeliveries?.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      if (parseInt(period, 10) === 0) {
        setDeliveries(
          sortedData.filter(
            (e) =>
              moment(new Date(e.createdAt)).format('MMM Do YY') === moment().format('MMM Do YY') &&
              (e.saleOrderClientName?.toLowerCase().includes(search.toLowerCase()) ||
                e.truckLicencePlate?.toLowerCase().includes(search.toLowerCase()))
          )
        )
      } else if (parseInt(period, 10) === 1) {
        setDeliveries(
          sortedData.filter(
            (e) =>
              moment(new Date(e.createdAt)).format('MMM Do YY') ===
                moment().subtract(1, 'days').format('MMM Do YY') &&
              (e.saleOrderClientName?.toLowerCase().includes(search.toLowerCase()) ||
                e.truckLicencePlate?.toLowerCase().includes(search.toLowerCase()))
          )
        )
      } else if (parseInt(period, 10) === 2) {
        setDeliveries(
          sortedData.filter(
            (e) =>
              new Date(e.createdAt).getTime() > new Date(moment().subtract(1, 'weeks')).getTime() &&
              (e.saleOrderClientName?.toLowerCase().includes(search.toLowerCase()) ||
                e.truckLicencePlate?.toLowerCase().includes(search.toLowerCase()))
          )
        )
      } else if (parseInt(period, 10) === 3) {
        setDeliveries(
          sortedData.filter(
            (e) =>
              new Date(e.createdAt).getTime() >
                new Date(moment().subtract(1, 'months')).getTime() &&
              (e.saleOrderClientName?.toLowerCase().includes(search.toLowerCase()) ||
                e.truckLicencePlate?.toLowerCase().includes(search.toLowerCase()))
          )
        )
      }
    }
  }, [data, period, search, offlineDeliveries])

  const handleExportTable = async (tableName) => {
    try {
      await db.open()

      // Récupère toutes les données de la table demandée
      const data = await db[tableName].toArray()

      // Crée un blob JSON à partir des données
      const blob = new Blob([JSON.stringify({ table: tableName, data }, null, 2)], {
        type: 'application/json',
      })

      // Déclenche le téléchargement
      const a = document.createElement('a')
      a.href = URL.createObjectURL(blob)
      a.download = `${tableName}_backup.json`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(a.href)

      console.log(`Table "${tableName}" exportée avec succès (${data.length} enregistrements)`)
    } catch (error) {
      console.error('Erreur lors de l’exportation de la table:', error)
    }
  }

  const handleFilterClick = () => {
    // Réinitialiser le timer à chaque clic
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current)
    }

    // Incrémenter le compteur
    clickCountRef.current += 1

    // Si 3 clics, ouvrir la modale d'export
    if (clickCountRef.current === 3) {
      openExportModal()
      clickCountRef.current = 0 // Réinitialiser après activation
    } else {
      // Reset du compteur après 1 seconde si pas le 3ème clic
      clickTimerRef.current = setTimeout(() => {
        clickCountRef.current = 0
      }, 1000)
    }
  }

  const openExportModal = () => {
    setShowExportModal(true)
    setPassword('')
    setPasswordError(false)
  }

  const closeExportModal = () => {
    setShowExportModal(false)
    setPassword('')
    setPasswordError(false)
  }

  const validatePassword = () => {
    if (password === EXPORT_PASSWORD) {
      handleExportTable('offlineOrdersExport')
      closeExportModal()
    } else {
      setPasswordError(true)
    }
  }

  // Gestion de la touche Entrée dans le champ mot de passe
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      validatePassword()
    }
  }

  const report = (id, status) => {
    updateStatus({
      variables: {
        id,
        status,
      },
      optimisticResponse: {
        updateDraftOutboundDelivery: {
          clientMutationId: null,
          draftOutboundDelivery: {
            id,
            status,
            __typename: 'DraftOutboundDelivery',
          },
          __typename: 'UpdateDraftOutboundDeliveryPayload',
        },
      },
    })
    db.deliveries.update(id, {
      status,
    })
  }

  const duplicate = (delivery) => {
    const items =
      delivery.draftOutboundDeliveryItemsByIdDraftOutboundDelivery?.nodes || delivery.products
    for (let index = 0; index < items.length; index++) {
      const element = items[index]
      element.delivered = ''
      element.quantity = null
    }

    db.offlineOrders.add({
      signature: '',
      process: 'validate',
      customer: delivery.saleOrderClientName,
      customerId: delivery.idClient,
      plate: delivery.truckLicencePlate,
      constructionNb: delivery.constructionSiteCode,
      address: delivery.constructionSiteAddress,
      city: delivery.constructionSiteCity,
      destinationSite: delivery.destinationSite,
      forwarder: delivery.forwardingAgent,
      externalReference: delivery.externalReference,
      outMaterials: items.filter(
        (e) => e.materialByIdMaterial.productCategoryByIdProductCategory.family === 'MATERIAL'
      ),
      inMaterials: items.filter(
        (e) => e.materialByIdMaterial.productCategoryByIdProductCategory.family === 'RUBBLE'
      ),
    })
    if (delivery.idInternalTransfert) {
      history.push('/evacuation')
    } else {
      history.push('/summary')
    }
  }

  // Nettoyer le timer quand le composant est démonté
  useEffect(() => {
    return () => {
      if (clickTimerRef.current) {
        clearTimeout(clickTimerRef.current)
      }
    }
  }, [])

  return (
    <div>
      <Header title={t('history')} />
      <div className="history">
        <div className="history-content">
          <div className="history-filter">
            <p style={{ cursor: 'pointer', userSelect: 'none' }} onClick={handleFilterClick}>
              {t('filter')}
            </p>
            <select value={period} onChange={(e) => setPeriod(e.target.value)}>
              <option value={0}>{t('today')}</option>
              <option value={1}>{t('yesterday')}</option>
              <option value={2}>{t('lastWeek')}</option>
              {/* <option value={3}>
                    {t('lastMonth')}
                  </option> */}
            </select>
            <input
              placeholder={t('search')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="history-item-content">
            {loading ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <div
                  style={{
                    height: '25vh',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    marginLeft: '20%',
                  }}
                >
                  <div className="ring" style={{ fontWeight: 'bolder' }} />
                  {t('delivery')}
                </div>
              </div>
            ) : (
              <p>
                {syncTime?.date &&
                  deliveries?.map((delivery) => (
                    <HistoryItem
                      synced={
                        new Date(delivery.createdAt).getTime() <
                          new Date(syncTime.date).getTime() || network
                      }
                      status={delivery.status}
                      key={delivery.id}
                      customer={delivery.saleOrderClientName}
                      products={
                        delivery.draftOutboundDeliveryItemsByIdDraftOutboundDelivery?.nodes ||
                        delivery.products
                      }
                      plateNumber={delivery.truckLicencePlate}
                      onDuplicate={() => duplicate(delivery)}
                      report={() =>
                        report(delivery.id, delivery.status === 'VALIDER' ? 'REDRESSER' : 'VALIDER')
                      }
                    />
                  ))}
              </p>
            )}
          </div>
        </div>
      </div>
      <Footer>
        <Button
          text={t('back')}
          appearance="secondary"
          type="outline"
          width="210px"
          onClick={() => history.push('/endPending')}
        />
      </Footer>

      {/* Modale d'exportation */}
      {showExportModal && (
        <Modal
          title={t('exportDatabase')}
          description={t('enterPasswordToExport')}
          cancelTxt={t('cancel')}
          okTxt={t('export')}
          onCancel={closeExportModal}
          onOk={validatePassword}
        >
          <div style={{ marginTop: '1rem' }}>
            {/* Continuer à utiliser le input classique pour éviter d'avoir un double input */}
            <input
              type="password"
              placeholder={t('password')}
              value={password}
              onChange={(e) => {
                setPassword(e.target.value)
                setPasswordError(false)
              }}
              onKeyPress={handleKeyPress}
              style={{
                width: '100%',
                padding: '8px',
                border: passwordError ? '1px solid red' : '1px solid #ccc',
                borderRadius: '4px',
              }}
              autoComplete="new-password"
              id="export-password-input"
            />
            {passwordError && (
              <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
                {t('incorrectPassword')}
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  )
}

export default History
